import { zodResolver } from '@hookform/resolvers/zod';
import { Allocations } from 'components/AllocationsTable/types';
import { capitalAction as rules } from 'further-ui/validations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useDefaultEntryValues from './useDefaultEntryValues';
import { CommitmentType } from 'further-types/lp/vehicle';
import { useLayoutContext } from 'contexts/LayoutContext';

const formSchema = rules.CreateCapitalAction;

export enum Steps {
  Details,
  Transactions,
  Review,
}

export type CapitalActionFormState = {
  allocations: Record<string, Array<Allocations>>;
  isEditForm: boolean;
};

export type CapitalActionFormValues = z.infer<typeof formSchema>;

function getStepValidationFields(step: Steps) {
  switch (step) {
    case Steps.Details:
      return ['vehicleId', 'name', 'ledgerDate', 'actionDueDate'] as const;
    case Steps.Transactions:
      return ['entries'] as const;
  }

  return [];
}

export default function useCapitalActionForm({
  id,
  vehicleId,
  defaultValues,
  hasUncalledEqualisations,
  onSubmitSuccess,
}: {
  id?: string;
  vehicleId: string;
  defaultValues?: CapitalActionFormValues;
  hasUncalledEqualisations: boolean;
  onSubmitSuccess?: (capitalActionId: string) => void;
}) {
  const { vehicle } = useLayoutContext();
  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;
  const hasCapitalActions = vehicle?.hasCapitalActions ?? false;
  const { defaultEntry } = useDefaultEntryValues({
    capitalContributionIsDefault: isCapitalAndLoan && !hasCapitalActions,
    equalisationIsDefault: hasUncalledEqualisations,
  });

  const formMethods = useForm<CapitalActionFormValues>({
    shouldFocusError: true,
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicleId,
      name: '',
      ledgerDate: undefined,
      actionDueDate: undefined,
      entries: [defaultEntry],
      transactions: [],
    },
    values: defaultValues
      ? {
          ...defaultValues,
          vehicleId,
          entries: defaultValues?.entries ?? [defaultEntry],
        }
      : undefined,
    criteriaMode: 'all',
    resetOptions: {
      keepDefaultValues: false,
    },
  });

  const validateStep = async (step: Steps) => {
    return await formMethods.trigger(getStepValidationFields(step));
  };

  const mutationOptions = useHandleTrpcMutation<string>()({
    onSuccess: (capitalActionId) => {
      onSubmitSuccess?.(capitalActionId);
    },
  });

  const createCapitalAction =
    api.capitalAction.create.useMutation(mutationOptions);

  const updateCapitalAction =
    api.capitalAction.update.useMutation(mutationOptions);

  const handleSubmit = (values: CapitalActionFormValues) => {
    if (id) {
      updateCapitalAction.mutate({ id, ...values });
    } else {
      createCapitalAction.mutate(values);
    }
  };

  return {
    formMethods,
    handleSubmit,
    validateStep,
    isLoading: createCapitalAction.isPending,
  };
}
